import { ReactNode, useState } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

export interface Column<T> {
  key: string;
  header: string;
  render: (item: T) => ReactNode;
  sortable?: boolean;
  width?: string;
}

export interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  onSort?: (key: string, direction: SortDirection) => void;
  emptyMessage?: string;
  rowClassName?: (item: T) => string;
}

export function Table<T>({
  columns,
  data,
  keyExtractor,
  onSort,
  emptyMessage = 'No data available',
  rowClassName,
}: TableProps<T>): JSX.Element {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSort = (columnKey: string): void => {
    const column = columns.find((c) => c.key === columnKey);
    if (column?.sortable !== true) return;

    let newDirection: SortDirection = 'asc';
    
    if (sortKey === columnKey) {
      if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else if (sortDirection === 'desc') {
        newDirection = null;
      }
    }

    setSortKey(newDirection !== null ? columnKey : null);
    setSortDirection(newDirection);

    if (onSort !== undefined) {
      onSort(columnKey, newDirection);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, columnKey: string): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSort(columnKey);
    }
  };

  return (
    <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 ${
                  column.width ?? ''
                } ${column.sortable === true ? 'cursor-pointer select-none hover:bg-gray-100' : ''}`}
                onClick={() => {
                  if (column.sortable === true) handleSort(column.key);
                }}
                onKeyDown={(e) => {
                  if (column.sortable === true) handleKeyDown(e, column.key);
                }}
                tabIndex={column.sortable === true ? 0 : undefined}
                role={column.sortable === true ? 'button' : undefined}
                aria-sort={
                  sortKey === column.key
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                <div className="flex items-center gap-2">
                  {column.header}
                  {column.sortable === true && (
                    <span aria-hidden="true">
                      {sortKey === column.key ? (
                        sortDirection === 'asc' ? (
                          '↑'
                        ) : (
                          '↓'
                        )
                      ) : (
                        <span className="text-gray-400">↕</span>
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-8 text-center text-sm text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((item) => (
              <tr key={keyExtractor(item)} className={`hover:bg-gray-50 ${rowClassName?.(item) ?? ''}`}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="whitespace-nowrap px-3 py-4 text-sm text-gray-900"
                  >
                    {column.render(item)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
